/* global google */
import { Controller } from '@hotwired/stimulus';
import Tagify from '@yaireo/tagify';
import debounce from 'lodash.debounce';

export default class extends Controller {
  static targets = ['input'];

  static values = {
    states: Object,
    delay: { type: Number, default: 300 },
  };

  initialize() {
    this.search = debounce(this.search.bind(this), this.delayValue);
    this.startPlaces = this.startPlaces.bind(this);
  }

  connect() {
    this.tagify = this.buildTagify();

    this.disabledObserver = new MutationObserver(() => this.syncDisabled());
    this.disabledObserver.observe(this.inputTarget, { attributes: true, attributeFilter: ['disabled'] });

    document.addEventListener('googlePlacesLoaded', this.startPlaces);
    if (window.googlePlacesLoaded) this.startPlaces();
  }

  disconnect() {
    document.removeEventListener('googlePlacesLoaded', this.startPlaces);
    this.disabledObserver?.disconnect();
    this.tagify?.destroy();
  }

  buildTagify() {
    const tagify = new Tagify(this.inputTarget, {
      mode: 'select',
      enforceWhitelist: true,
      // Labels contain a comma; Tagify reads this as a regular expression.
      delimiters: '\\|',
      whitelist: this.inputTarget.value ? [this.inputTarget.value] : [],
      dropdown: { enabled: 1, maxItems: 8, closeOnSelect: true },
      originalInputValueFormat: (values) => values.map((item) => item.value).join(''),
    });
    tagify.on('input', (event) => this.search(event.detail.value));

    return tagify;
  }

  // Tagify reads the disabled state only once, so a re-enabled field gets a fresh one.
  syncDisabled() {
    if (this.inputTarget.disabled) {
      this.tagify.setDisabled(true);
    } else {
      this.tagify.destroy();
      this.tagify = this.buildTagify();
    }
  }

  startPlaces() {
    this.service ||= new google.maps.places.AutocompleteService();
    this.sessionToken ||= new google.maps.places.AutocompleteSessionToken();
  }

  async search(value) {
    if (!this.service || !value) return;

    this.tagify.loading(true);
    try {
      const { predictions } = await this.service.getPlacePredictions({
        input: value,
        language: 'en',
        types: ['(cities)'],
        componentRestrictions: { country: 'us' },
        sessionToken: this.sessionToken,
      });
      this.tagify.whitelist = [...new Set(predictions.map((prediction) => this.label(prediction)).filter(Boolean))];
      this.tagify.loading(false).dropdown.show(value);
    } catch {
      this.tagify.loading(false).dropdown.hide();
    }
  }

  label(prediction) {
    const [city, stateCode] = prediction.terms.map((term) => term.value);
    const state = this.statesValue[stateCode];

    return city && state ? `${city}, ${state}` : null;
  }
}
